import { useRecentSearches } from '@/entities/drug/hooks/useRecentSearches';
import { Button } from '@/shared/components/ui/button';
import { Icon } from '@/shared/components/ui/icon';
import { SearchInput } from '@/shared/components/ui/input';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { cn } from '@/shared/lib/utils';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';

const PLACEHOLDER = '제품명 · 성분명 · 제약사로 검색';

/**
 * GNB 의약품 검색 (Figma header 495:6572 · 기획↔디자인 변경 912:6416 "의약품 검색 진입")
 * - 어느 화면에서든 검색창으로 검색한다. 실행하면 의약품 검색 결과(/drugs?q=)로 간다.
 * - 포커스하면 `최근 검색` 카드(804:4435)가 아래에 열린다. 최근 검색은 브라우저에만 저장 (useRecentSearches · 최대 10건).
 * - 의약품 검색 화면에서는 주소의 q를 검색창에 보여 준다.
 */
export const GnbSearch = ({ className }: { className?: string }) => {
  const navigate = useNavigate();
  const recent = useRecentSearches();
  const routeQuery = useRouterState({
    select: (s) =>
      s.location.pathname === ROUTE_PATHS.DRUGS
        ? ((s.location.search as { q?: string }).q ?? '')
        : '',
  });
  const [value, setValue] = useState(routeQuery);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  // 다른 화면으로 가거나 결과 화면의 q가 바뀌면 검색창도 따라간다
  useEffect(() => setValue(routeQuery), [routeQuery]);

  const run = (keyword: string) => {
    const q = keyword.trim();
    if (!q) return;
    recent.add(q);
    setValue(q);
    setOpen(false);
    inputRef.current?.blur();
    void navigate({ to: ROUTE_PATHS.DRUGS, search: { q } });
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    run(value);
  };

  const showRecent = open && recent.items.length > 0;

  return (
    <form
      role="search"
      onSubmit={onSubmit}
      className={cn('relative', className)}
      onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
    >
      <SearchInput
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        placeholder={PLACEHOLDER}
        aria-label="의약품 검색"
        aria-expanded={showRecent}
        aria-controls={showRecent ? listId : undefined}
        enterKeyHint="search"
        autoComplete="off"
        rightSlot={
          value && (
            // 글자가 있을 때만 지우기 버튼 (Figma 결과 화면 GNB · delete-circle)
            <button
              type="button"
              aria-label="검색어 지우기"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setValue('');
                inputRef.current?.focus();
              }}
              className="flex text-fg-light"
            >
              <Icon name="delete-circle-solid-24" />
            </button>
          )
        }
      />

      {showRecent && (
        // 카드 안을 눌러도 검색창 포커스(=카드 열림)가 유지되도록 mousedown 기본 동작을 막는다
        <div
          id={listId}
          onMouseDown={(e) => e.preventDefault()}
          className="absolute top-full right-0 left-0 z-50 mt-2 flex flex-col gap-4 rounded-16 border border-grey-900 bg-surface-white p-6 shadow-800"
        >
          <div className="flex items-center justify-between">
            <p className="text-subtitle-18-b text-fg-primary">최근 검색</p>
            <Button
              type="button"
              variant="text"
              size="small"
              className="text-fg-tertiary"
              onClick={recent.clear}
            >
              전체 삭제
            </Button>
          </div>
          <ul className="flex flex-col gap-3">
            {recent.items.map((k) => (
              <li key={k} className="flex items-center gap-6">
                <button
                  type="button"
                  onClick={() => run(k)}
                  className="min-w-0 flex-1 truncate text-left text-body-14-r text-fg-secondary hover:text-fg-primary"
                >
                  {k}
                </button>
                <Button
                  type="button"
                  variant="text"
                  size="tiny"
                  aria-label={`${k} 삭제`}
                  leftIcon={<Icon name="cancel-line-14" />}
                  onClick={() => recent.remove(k)}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </form>
  );
};
